// Load a floor plan file (JPG, PNG, SVG, PDF) into an HTMLImageElement / canvas
// Returns { image: HTMLImageElement, width, height }

export async function loadFloorPlanFile(file) {
  const type = file.type || "";
  const name = file.name || "";

  if (isPDF(file)) {
    const pdf = await openPDF(file);
    return await renderPDFPage(pdf, 1);
  }
  if (type === "image/svg+xml" || name.toLowerCase().endsWith(".svg")) {
    return await loadSVG(file);
  }
  return await loadRaster(file);
}

export const isPDF = (file) => (file.type || "") === "application/pdf" || (file.name || "").toLowerCase().endsWith(".pdf");

function loadRaster(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ image: img, width: img.naturalWidth, height: img.naturalHeight, url });
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function loadSVG(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const svgText = reader.result;
      const blob = new Blob([svgText], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || 1200;
        const h = img.naturalHeight || 900;
        resolve({ image: img, width: w, height: h, url });
      };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

async function getPdfjs() {
  const pdfjs = await import("pdfjs-dist/build/pdf");
  // Use CDN worker to avoid bundling issues
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;
  return pdfjs;
}

// Open a PDF file; returns pdfjs document (use .numPages)
export async function openPDF(file) {
  const pdfjs = await getPdfjs();
  const arrayBuffer = await file.arrayBuffer();
  return await pdfjs.getDocument({ data: arrayBuffer }).promise;
}

async function rasterizePage(pdf, pageNum, scale) {
  const page = await pdf.getPage(pageNum);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
  return canvas;
}

// Small thumbnail data URL for a page
export async function renderPDFThumbnail(pdf, pageNum, targetWidth = 220) {
  const page = await pdf.getPage(pageNum);
  const base = page.getViewport({ scale: 1 });
  const canvas = await rasterizePage(pdf, pageNum, targetWidth / base.width);
  return canvas.toDataURL("image/jpeg", 0.8);
}

// Full-resolution render of one page as an HTMLImageElement
export async function renderPDFPage(pdf, pageNum) {
  const canvas = await rasterizePage(pdf, pageNum, 2);
  const dataUrl = canvas.toDataURL("image/png");
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ image: img, width: canvas.width, height: canvas.height, url: dataUrl });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export async function loadFromURL(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve({ image: img, width: img.naturalWidth, height: img.naturalHeight, url });
    img.onerror = reject;
    img.src = url;
  });
}
